import React, { createContext, useCallback, useMemo, useContext } from 'react'
import { useHistory } from 'react-router'
import { useAppSelector } from '../../../redux-types'
import { useOnApproved, useOnDenied } from '../messaging'
import useSearchParams from '../../hooks/useSearchParams'
import {
  JoinToSpeakIDSearchParamName,
  JoinToSpeakPresenterTokenSearchParamName,
  JoinToSpeakQuestionSearchParamName,
} from '../constants'
import { useCancelGuestRequestMutation } from '../use-cancel-guest-request-mutation'
import { useRequestGuestMutation } from '../use-request-guest-mutation'
import type {
  JoinToSpeakAudienceContextValue,
  JoinToSpeakAudienceProviderProps,
} from './types'
import { useIsJoiningToSpeak } from '..'

const JoinToSpeakAudienceContext =
  createContext<JoinToSpeakAudienceContextValue | null>(null)

const JoinToSpeakAudienceProvider = ({
  children,
  sessionId,
  token,
}: JoinToSpeakAudienceProviderProps) => {
  const requestGuestMutation = useRequestGuestMutation()
  const cancelGuestRequestMutation = useCancelGuestRequestMutation()
  const searchParams = useSearchParams()
  const joinToSpeakID = searchParams.get(JoinToSpeakIDSearchParamName)
  const joinToSpeakPresenterToken = searchParams.get(
    JoinToSpeakPresenterTokenSearchParamName
  )
  const [, setIsJoiningToSpeak] = useIsJoiningToSpeak(sessionId)
  const history = useHistory()
  const name = useAppSelector(
    (state) => state.webinarAudienceRoom.localParticipantName ?? undefined
  )

  const requestToJoin = useCallback(
    async (question: string) => {
      const response = await requestGuestMutation.mutateAsync({
        sessionId,
        token,
        name,
        question: question.trim(),
      })

      return {
        joinToSpeakID: response.liveSpeakerId,
        joinToSpeakPresenterToken: response.presenterToken,
      }
    },
    [requestGuestMutation, sessionId, token, name]
  )

  const cancelRequestToJoin = useCallback(async () => {
    if (joinToSpeakPresenterToken === null || joinToSpeakID === null) {
      return
    }

    await cancelGuestRequestMutation.mutateAsync({
      presenterToken: joinToSpeakPresenterToken,
      sessionId,
      speakerId: joinToSpeakID,
    })
  }, [
    cancelGuestRequestMutation,
    joinToSpeakPresenterToken,
    sessionId,
    joinToSpeakID,
  ])

  useOnApproved(async (message) => {
    if (message.data.joinToSpeakID === joinToSpeakID) {
      const url = new URL(message.data.roomLink)
      url.searchParams.set(JoinToSpeakIDSearchParamName, joinToSpeakID)
      url.searchParams.set('ff', '1')
      setIsJoiningToSpeak(true)

      window.location.href = url.toString()
    }
  })

  useOnDenied((message) => {
    if (message.data.joinToSpeakID === joinToSpeakID) {
      searchParams.delete(JoinToSpeakIDSearchParamName)
      searchParams.delete(JoinToSpeakPresenterTokenSearchParamName)
      searchParams.delete(JoinToSpeakQuestionSearchParamName)
      history.replace({ search: searchParams.toString() })
    }
  })

  const context = useMemo<JoinToSpeakAudienceContextValue>(
    () => ({
      requestToJoin,
      cancelRequestToJoin,
    }),
    [requestToJoin, cancelRequestToJoin]
  )

  return (
    <JoinToSpeakAudienceContext.Provider value={context}>
      {children}
    </JoinToSpeakAudienceContext.Provider>
  )
}

function useJoinToSpeakAsAudience() {
  const context = useContext(JoinToSpeakAudienceContext)

  if (context === null) {
    throw new Error(
      'useJoinToSpeakAsAudience must be used within a JoinToSpeakAudienceProvider'
    )
  }

  return context
}

export { JoinToSpeakAudienceProvider, useJoinToSpeakAsAudience }
